import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { PaginatedResponse } from '@services/api/generic-api-client.service';
import { IApiQueryOptions } from '@services/api/models/api-query-params.model';
import { DateFilter, DateFilterOperator } from '@services/api/models/date-filter.model';
import { LogService } from '@services/util/log.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { inject, Injectable, signal } from '@angular/core';

/**
 * Service to handle booking data loading for parking request forms
 */
@Injectable({
  providedIn: 'root'
})
export class ParkingRequestBookingDataService {
  private bookingService = inject(BookingClientService);
  private log = inject(LogService);

  // State signals
  private readonly bookingsSubject = new BehaviorSubject<IBookingModel[]>([]);
  readonly bookings$ = this.bookingsSubject.asObservable();

  private readonly filteredBookingsSubject = new BehaviorSubject<IBookingModel[]>([]);
  readonly filteredBookings$ = this.filteredBookingsSubject.asObservable();

  private readonly loadingMoreBookingsSubject = new BehaviorSubject<boolean>(false);
  readonly loadingMoreBookings$ = this.loadingMoreBookingsSubject.asObservable();

  // Pagination state
  private currentPage = 1;
  private hasMorePages = true;
  private searchTerm = '';
  private readonly today = new Date();

  constructor() {
    this.today.setHours(0, 0, 0, 0);
  }

  /**
   * Load bookings with filtering and pagination
   */
  loadBookings(reset: boolean = true): Observable<IBookingModel[]> {
    if (reset) {
      this.currentPage = 1;
      this.bookingsSubject.next([]);
      this.hasMorePages = true;
    } else {
      this.loadingMoreBookingsSubject.next(true);
    }

    // Create a date filter for bookings ending after today
    const dateFilter: DateFilter = {
      date: this.today,
      operator: 'after' as DateFilterOperator
    };

    // Build query options with date filtering
    const queryOptions: IApiQueryOptions = {
      filters: {},
      dateFilters: {
        end_date: dateFilter
      },
      pagination: {
        page: this.currentPage,
        itemsPerPage: 30
      }
    };

    // Add name filter if search text is provided
    if (this.searchTerm) {
      queryOptions.filters = {
        ...queryOptions.filters,
        name: this.searchTerm
      };
    }

    return new Observable((observer) => {
      this.bookingService.getItems('', queryOptions).subscribe({
        next: (response: PaginatedResponse<IBookingModel>) => {
          // DEBUG: Log the response to see what we're getting
          this.log.debug('Booking API Response:', response);
          this.log.debug('Response items array:', response.items);
          this.log.debug('Response totalItems:', response.totalItems);

          const newBookings = reset ? response.items : [...this.bookingsSubject.value, ...response.items];

          this.log.debug('New bookings array:', newBookings);

          this.bookingsSubject.next(newBookings);
          this.updateFilteredBookings();

          this.hasMorePages = this.currentPage < Math.ceil(response.totalItems / 30);
          this.loadingMoreBookingsSubject.next(false);

          observer.next(newBookings);
          observer.complete();
        },
        error: (error: Error) => {
          this.log.error('Failed to load bookings', error);
          this.loadingMoreBookingsSubject.next(false);
          observer.error(error);
        }
      });
    });
  }

  /**
   * Load more bookings (pagination)
   */
  loadMoreBookings(): void {
    if (!this.hasMorePages || this.loadingMoreBookingsSubject.value) {
      return;
    }

    this.currentPage++;
    this.loadBookings(false).subscribe();
  }

  /**
   * Search bookings
   */
  searchBookings(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.loadBookings(true).subscribe();
  }

  /**
   * Update filtered bookings based on search term
   */
  private updateFilteredBookings(): void {
    const allBookings = this.bookingsSubject.value;
    if (!this.searchTerm) {
      this.filteredBookingsSubject.next(allBookings);
      return;
    }

    const filtered = allBookings.filter(
      (booking) =>
        booking.name?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        booking.start_date?.includes(this.searchTerm) ||
        booking.end_date?.includes(this.searchTerm)
    );
    this.filteredBookingsSubject.next(filtered);
  }

  /**
   * Load a specific booking by ID
   */
  loadBooking(bookingId: string): Observable<IBookingModel | null> {
    const id = parseInt(bookingId.split('/').pop() || '0', 10);
    return this.bookingService.getItem(id);
  }

  /**
   * Reset service state
   */
  reset(): void {
    this.bookingsSubject.next([]);
    this.filteredBookingsSubject.next([]);
    this.loadingMoreBookingsSubject.next(false);
    this.currentPage = 1;
    this.hasMorePages = true;
    this.searchTerm = '';
  }
}
